import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getSystemStatus,
  onSystemRecovered,
  serverNow,
  startSystemStatus,
  useSystemStatus,
  type BakimDurumu,
} from '../../data/system-status.js';
import { queryKeys } from '../../data/query.js';
import { startMaintenanceFavicon } from './maintenance-favicon.js';
import { MaintenanceScreen, type MaintenanceAudience } from './maintenance-screen.js';

/**
 * Uygulamanın bakım katmanı (main.tsx kökte bir kez çizer; kabuk da genel sayfalar da altında):
 *  - kesinti ya da planlı bakımda ekranın ÜSTÜNE tam sayfa B ekranı — alttaki ağaç sökülmez,
 *    yarım bir form kaybolmaz; sistem dönünce katman kalkar ve sorgular tazelenir;
 *  - duyuruda kabuğun üstünde geri sayım şeridi (kabuk şerit kadar aşağı itilir, örtülmez);
 *  - kesintisiz güncellemeden sonra "DENT-OS güncellendi · Yenile" bildirimi.
 * Görünüm tasarım belgesindeki onaylı taslaklardır (2026-09-22).
 */
const T = {
  warnSoft: '#FCEFD0',
  warnLine: '#F3DDA6',
  warnInk: '#6B4604',
  warnInk2: '#8A5B06',
  accent: '#1A1B1D',
  font: '"Plus Jakarta Sans", -apple-system, "Segoe UI", system-ui, sans-serif',
};
export const BAND_HEIGHT = 38;

const CSS = `
.bkm-layer { position: fixed; inset: 0; z-index: 1000; overflow: auto; }
.bkm-band { position: fixed; top: 0; left: 0; right: 0; z-index: 950; height: ${BAND_HEIGHT}px; display: flex; align-items: center; gap: 10px; padding: 0 16px; background: ${T.warnSoft}; color: ${T.warnInk}; border-bottom: 1px solid ${T.warnLine}; font: 500 12.5px/1.3 ${T.font}; box-sizing: border-box; }
.bkm-band svg { flex: 0 0 auto; }
.bkm-band b { font-weight: 700; font-variant-numeric: tabular-nums; }
.bkm-band-msg { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bkm-sr { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.bkm-band-side { margin-left: auto; flex: 0 0 auto; font-size: 11.5px; font-weight: 600; color: ${T.warnInk2}; opacity: .85; }
body.bkm-duyuru { padding-top: ${BAND_HEIGHT}px; }
body.bkm-duyuru .app, body.bkm-duyuru .phone-wrap { height: calc(100vh - ${BAND_HEIGHT}px); }
.bkm-toast { position: fixed; left: 50%; bottom: 24px; z-index: 900; transform: translateX(-50%); display: flex; align-items: center; gap: 10px; padding: 8px 8px 8px 10px; border-radius: 14px; background: ${T.accent}; color: #fff; font: 500 12.5px/1.3 ${T.font}; white-space: nowrap; box-shadow: 0 14px 40px -12px rgba(15,23,42,.45); animation: bkm-toast-in .45s cubic-bezier(.2,.8,.2,1) both; }
.bkm-toast img { width: 22px; height: 22px; border-radius: 6px; display: block; }
.bkm-toast small { color: rgba(255,255,255,.62); font-size: 11.5px; }
.bkm-toast button { border: 0; border-radius: 9px; background: #fff; color: ${T.accent}; font: 600 12px/1 ${T.font}; padding: 7px 12px; cursor: pointer; transition: transform .12s cubic-bezier(.2,.8,.2,1), background-color .15s; }
.bkm-toast button:hover { background: #EEF1F6; }
.bkm-toast button:active { transform: scale(.97); }
.bkm-toast button:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
body.bkm-guncel .toasts { bottom: 84px; }
@keyframes bkm-toast-in { from { opacity: 0; transform: translate(-50%, 10px); } to { opacity: 1; transform: translate(-50%, 0); } }
@media (max-width: 767px) {
  .bkm-toast { bottom: calc(88px + env(safe-area-inset-bottom, 0px)); }
  .bkm-band-side { display: none; }
}
@media (prefers-reduced-motion: reduce) { .bkm-toast { animation: none; } .bkm-toast button { transition: none; } }
`;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Açık bir pencere/çekmece/diyalog varken sayfa yenilenmez: içinde yarım bir form olabilir. */
export function hasOpenDialog(): boolean {
  return (
    document.querySelector('.modal-back, .drawer-back, [role="dialog"], [role="alertdialog"]') !==
    null
  );
}

/**
 * Kabuk her sayfa geçişinde çağırır: yeni sürüm bekliyorsa ve açık pencere yoksa sayfa yeni
 * sürümle yeniden yüklenir ("Yenile"ye basılmasa da). Yenilendiyse true.
 */
export function reloadIfUpdatePending(
  reload: () => void = () => window.location.reload(),
): boolean {
  if (getSystemStatus().update === null || hasOpenDialog()) return false;
  reload();
  return true;
}

/** Sunucu saatine göre şimdi; `active` iken saniyede bir tazelenir. */
function useServerClock(active: boolean): number {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    if (!active) return undefined;
    setNow(serverNow());
    const id = window.setInterval(() => setNow(serverNow()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h} sa ${pad(m)} dk` : `${pad(m)}:${pad(s)}`;
}

function CountdownBand({ planned, now }: { planned: BakimDurumu; now: number }) {
  const left = Date.parse(planned.baslangic) - now;
  const duration = planned.tahminiBitis
    ? Math.round((Date.parse(planned.tahminiBitis) - Date.parse(planned.baslangic)) / 60_000)
    : null;
  // Ekran okuyucu her saniye değil, dakikada bir bilgilendirilir.
  const minutes = Math.max(1, Math.ceil(left / 60_000));
  useEffect(() => {
    document.body.classList.add('bkm-duyuru');
    return () => document.body.classList.remove('bkm-duyuru');
  }, []);
  return (
    <div className="bkm-band" role="region" aria-label="Bakım duyurusu">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <span className="bkm-band-msg">
        Sistem <b aria-hidden="true">{formatCountdown(left)}</b> sonra kısa bakıma alınacak. Açık
        işlemlerinizi kaydedin.
      </span>
      <span className="bkm-sr" aria-live="polite">
        {`Sistem ${minutes} dakika sonra kısa bakıma alınacak.`}
      </span>
      {duration !== null && duration > 0 && (
        <span className="bkm-band-side">Tahmini süre {duration} dk</span>
      )}
    </div>
  );
}

function UpdateToast({ surum, audience }: { surum: string; audience: MaintenanceAudience }) {
  useEffect(() => {
    document.body.classList.add('bkm-guncel');
    return () => document.body.classList.remove('bkm-guncel');
  }, []);
  return (
    // Hastanın sayfasında satıcı adı geçmez (2 Ekim 2026): "Sayfa güncellendi".
    <div
      className="bkm-toast"
      role="status"
      aria-label={audience === 'public' ? 'Yeni sürüm hazır' : `DENT-OS ${surum} hazır`}
    >
      <img src="/icon-192.png" alt="" width={22} height={22} />
      <span>
        {audience === 'public' ? 'Sayfa' : 'DENT-OS'} güncellendi <small>· yeni sürüm hazır</small>
      </span>
      <button type="button" onClick={() => window.location.reload()}>
        Yenile
      </button>
    </div>
  );
}

export function MaintenanceLayer({
  audience,
  autoReloadOnUpdate = false,
}: {
  audience: MaintenanceAudience;
  /**
   * Gözetimsiz ekranlar (bekleme salonu, afiş): önünde kimse "Yenile"ye basmaz. Yeni sürüm
   * gelince açık pencere yoksa kendiliğinden yenilenir.
   */
  autoReloadOnUpdate?: boolean;
}) {
  const status = useSystemStatus();
  const queryClient = useQueryClient();
  // Oturum önbellekte varsa bakan personeldir (giriş ekranından girmiş olsa da). Önbellek yalnız
  // OKUNUR: genel sayfalar (/randevu, /odeme) katman yüzünden /auth/me istemez.
  const seenBy: MaintenanceAudience = queryClient.getQueryData(queryKeys.me) ? 'clinic' : audience;
  const overlay = status.outage || status.plannedActive;
  const band = !overlay && status.planned?.mod === 'duyuru';
  const now = useServerClock(overlay || band);

  useEffect(() => startSystemStatus(), []);
  useEffect(
    () =>
      onSystemRecovered(() => {
        void queryClient.invalidateQueries();
      }),
    [queryClient],
  );
  // Katman açıkken alttaki uygulama klavye ve ekran okuyucu için devre dışı; sekme bakım hâlinde.
  useEffect(() => {
    if (!overlay) return undefined;
    const app = document.getElementById('app');
    app?.setAttribute('inert', '');
    app?.setAttribute('aria-hidden', 'true');
    const stopFavicon = startMaintenanceFavicon(prefersReducedMotion(), seenBy);
    return () => {
      app?.removeAttribute('inert');
      app?.removeAttribute('aria-hidden');
      stopFavicon();
    };
  }, [overlay, seenBy]);

  useEffect(() => {
    if (autoReloadOnUpdate && status.update && !overlay) reloadIfUpdatePending();
  }, [autoReloadOnUpdate, status.update, overlay]);

  const planned = status.planned;
  const view = status.plannedActive || planned?.mod === 'planli' ? 'planli' : 'kesinti';
  return createPortal(
    <>
      <style>{CSS}</style>
      {band && planned && <CountdownBand planned={planned} now={now} />}
      {status.update && !overlay && !autoReloadOnUpdate && (
        <UpdateToast surum={status.update.surum} audience={seenBy} />
      )}
      {overlay && (
        <div className="bkm-layer" role="alertdialog" aria-modal="true" aria-labelledby="bkm-title">
          <MaintenanceScreen view={view} planned={planned} now={now} audience={seenBy} />
        </div>
      )}
    </>,
    document.body,
  );
}
